import {
  GLYPH_MAP_OPERATIONAL_ACTION,
  GLYPH_MAP_TELEMETRY_EVENT,
  addPageAction,
  forwardGlyphMapOperationalRecord,
  setCustomAttribute
} from '../../util/new_relic.js';
import { isPhone } from '../../util/is_phone.js';
import { TAB_NAVIGATION_ACTIVATED_EVENT } from '../../util/tab_navigation_events.js';
import { monotonicNowMs, reportSafely } from './telemetryRuntime.js';

export { GLYPH_MAP_OPERATIONAL_ACTION, GLYPH_MAP_TELEMETRY_EVENT };
export { monotonicNowMs, reportSafely };

type GlyphMapEvent = 'fdp_view' | 'tab_activated';

function familyPage(): HTMLElement | null {
  return document.querySelector('[data-glyph-map-family-slug]');
}

function glyphMapEligible(page: HTMLElement): boolean {
  return page.dataset.glyphMapEligible === 'true' && !isPhone();
}

function pageAttributes(page: HTMLElement) {
  const familySlug = page.dataset.glyphMapFamilySlug;
  if (!familySlug) {
    throw new Error('Glyph Map analytics: FDP is missing its family slug');
  }
  page.dataset.glyphMapVisitId ??= crypto.randomUUID();
  return {
    familySlug,
    glyphMapVisitId: page.dataset.glyphMapVisitId,
    glyphMapFlagEnabled: page.dataset.glyphMapEnabled === 'true',
    glyphMapEligible: glyphMapEligible(page)
  };
}

function optionalPageAttributes(): Partial<ReturnType<typeof pageAttributes>> {
  const page = familyPage();
  if (!page?.dataset.glyphMapFamilySlug) {
    return {};
  }
  return pageAttributes(page);
}

/**
 * Emits one settled host operational record to `typekit.glyph_map.operational`.
 * @param attributes - Settled operational payload fields.
 */
export function reportGlyphMapOperational(
  attributes: Record<string, unknown>
): void {
  reportSafely(() => {
    const pageAttrs = optionalPageAttributes();
    forwardGlyphMapOperationalRecord({
      schemaVersion: 1,
      source: 'host',
      ...pageAttrs,
      ...attributes
    });
  });
}

function reportGlyphMapEvent(
  eventName: GlyphMapEvent,
  attributes: ReturnType<typeof pageAttributes>
): void {
  reportSafely(() => {
    addPageAction(`typekit.glyph_map.${eventName}`, {
      ...attributes,
      feature: 'glyph_map'
    });
  });
}

function handleTabActivation(event: Event): void {
  if (event instanceof CustomEvent && typeof event.detail === 'string') {
    trackGlyphMapTabActivation(event.detail);
  }
}

/**
 * Records one FDP baseline and correlates subsequent Browser agent events.
 */
export function initGlyphMapAnalytics(): void {
  reportSafely(() => {
    const page = familyPage();
    if (!page || page.dataset.glyphMapTracked === 'true') {
      return;
    }
    window.addEventListener(TAB_NAVIGATION_ACTIVATED_EVENT, handleTabActivation);
    page.dataset.glyphMapTracked = 'true';
    const attributes = pageAttributes(page);
    reportSafely(() => {
      for (const [name, value] of Object.entries(attributes)) {
        setCustomAttribute(name, value);
      }
    });
    reportGlyphMapEvent('fdp_view', attributes);
  });
}

/**
 * Records explicit Glyph Map tab activation, including repeated activations.
 * @param tabId - Tab selected by the navigation button's activation handler.
 */
export function trackGlyphMapTabActivation(tabId: string): void {
  reportSafely(() => {
    if (tabId !== 'glyph-map') {
      return;
    }
    const page = familyPage();
    if (page && glyphMapEligible(page)) {
      reportGlyphMapEvent('tab_activated', pageAttributes(page));
    }
  });
}
